import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, CheckCircle2, AlertOctagon, Trash2, Loader2,
  ShieldCheck, Infinity as InfinityIcon, TimerReset, Ban, IndianRupee,
  Gift,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { formatBusinessType } from "@/lib/businessTypes";

const STATUS_STYLE = {
  pending: "text-amber-400 border-amber-400/40 bg-amber-500/10",
  active: "text-emerald-400 border-emerald-400/40 bg-emerald-500/10",
  suspended: "text-red-400 border-red-400/40 bg-red-500/10",
};

// Defensive: legacy vendors saved before the `status` field existed have
// status=null — treat them as "pending" for display + actions.
const normalizeStatus = (raw) => {
  const s = (raw || "").toString().trim().toLowerCase();
  if (s === "active" || s === "pending" || s === "suspended") return s;
  return "pending";
};

export default function AdminVendorDetailPage({ superadmin = false }) {
  const { id } = useParams();
  const { admin } = useAdminAuth();
  const [vendor, setVendor] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [v, c] = await Promise.all([
        api.adminVendor(id),
        api.adminVendorCampaigns(id),
      ]);
      setVendor(v.data);
      setCampaigns(c.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const activate = async () => {
    await api.adminActivateVendor(id);
    toast.success("Vendor activated");
    load();
  };
  const saveNote = async () => {
    if (!note.trim()) return;
    await api.adminAddNote(id, { note: note.trim() });
    toast.success("Note saved");
    setNote("");
  };
  const suspend = async () => {
    const reason = window.prompt("Reason for suspension?") || "";
    if (!reason) return;
    await api.superadminSuspendVendor(id, reason);
    toast.success("Vendor suspended");
    load();
  };
  const reactivate = async () => {
    await api.superadminReactivateVendor(id);
    toast.success("Vendor reactivated");
    load();
  };
  const remove = async () => {
    if (!window.confirm(`Permanently delete vendor "${vendor?.business}"? This will remove ALL their data.`)) return;
    await api.superadminDeleteVendor(id);
    toast.success("Vendor deleted");
    window.location.href = superadmin ? "/superadmin/vendors" : "/admin/vendors";
  };

  if (loading || !vendor) {
    return <div className="min-h-[400px] grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  }

  const canSuperAct = admin?.role === "superadmin";

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={superadmin ? "/superadmin/vendors" : "/admin/vendors"}
          className="inline-flex items-center text-sm text-muted-foreground hover:underline"
          data-testid="admin-vendor-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to vendors
        </Link>
        <h1 className="font-heading text-3xl font-semibold mt-1" data-testid="admin-vendor-detail-name">
          {vendor.business}
        </h1>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className={`capitalize ${STATUS_STYLE[normalizeStatus(vendor.status)]}`} data-testid="admin-vendor-detail-status">{normalizeStatus(vendor.status)}</Badge>
          <span className="text-sm text-muted-foreground" data-testid="vendor-detail-business-type">{formatBusinessType(vendor) || "—"} • {vendor.city || "—"}</span>
        </div>
      </div>

      <Card className="p-5">
        <h2 className="font-heading text-base font-semibold mb-3">Vendor info</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Row label="Owner">{vendor.name}</Row>
          <Row label="Email">{vendor.email}</Row>
          <Row label="Phone">{vendor.phone}</Row>
          <Row label="GST">{vendor.gst_number || "—"}</Row>
          <Row label="City">{vendor.city || "—"}</Row>
          <Row label="Joined">{vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : ""}</Row>
          <Row label="Activated">{vendor.activated_at ? new Date(vendor.activated_at).toLocaleDateString() : "—"}</Row>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Campaigns" value={vendor.campaigns_count} testid="vendor-stat-campaigns" />
          <Stat label="Leads" value={vendor.leads_count} testid="vendor-stat-leads" />
          <Stat label="Coupons" value={vendor.coupons_count} testid="vendor-stat-coupons" />
          <Stat label="Redeemed" value={vendor.redeemed_count} testid="vendor-stat-redeemed" />
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {normalizeStatus(vendor.status) === "pending" && (
            <Button onClick={activate} data-testid="admin-detail-activate">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Activate subscription
            </Button>
          )}
          {canSuperAct && normalizeStatus(vendor.status) === "active" && (
            <Button variant="outline" onClick={suspend} data-testid="sa-suspend-button">
              <AlertOctagon className="h-4 w-4 mr-1" /> Suspend
            </Button>
          )}
          {canSuperAct && normalizeStatus(vendor.status) === "suspended" && (
            <Button variant="outline" onClick={reactivate} data-testid="sa-reactivate-button">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Reactivate
            </Button>
          )}
          {canSuperAct && (
            <Button variant="destructive" onClick={remove} data-testid="sa-delete-button">
              <Trash2 className="h-4 w-4 mr-1" /> Delete vendor
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="font-heading text-base font-semibold mb-3">Campaigns</h2>
        {campaigns.length === 0 ? (
          <p className="text-sm text-muted-foreground">No campaigns yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {campaigns.map((c) => (
              <li key={c.id} className="py-2 flex items-center justify-between text-sm">
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{c.name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{c.theme} • {c.slices?.length || 0} slices</span>
                </div>
                <Badge variant="outline" className="capitalize mr-2" data-testid="admin-vendor-campaign-status">{c.status}</Badge>
                {(c.status === "pending_payment" || c.status === "trial_ended") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const reason = window.prompt(`Waive ₹499 for "${c.name}"?\nReason (optional):`, "");
                      if (reason === null) return;
                      try {
                        await api.adminWaiveCampaign(c.id, reason);
                        toast.success("Campaign waived");
                        load();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Failed");
                      }
                    }}
                    data-testid="admin-vendor-campaign-waive"
                  >
                    <IndianRupee className="h-3 w-3 mr-1" /> Waive
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <AccessOverrideCard vendor={vendor} onChanged={load} />

      <IntakeProfileCard vendorId={id} />

      {canSuperAct && (
        <WalletAdjustCard vendor={vendor} onChanged={load} />
      )}

      {canSuperAct && (
        <GrantPromoCreditCard vendor={vendor} onChanged={load} />
      )}

      <Card className="p-5">
        <h2 className="font-heading text-base font-semibold mb-3">Add support note</h2>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note about this vendor..." data-testid="admin-note-textarea" />
        <div className="mt-2 flex justify-end">
          <Button onClick={saveNote} data-testid="admin-note-save">Save note</Button>
        </div>
      </Card>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div>
    <span className="text-muted-foreground mr-1">{label}:</span>
    <span>{children}</span>
  </div>
);
const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg bg-secondary p-3">
    <p className="text-xs uppercase text-muted-foreground">{label}</p>
    <p className="font-heading text-2xl font-semibold mt-0.5 tabular-nums" data-testid={testid}>{value ?? 0}</p>
  </div>
);

// ---------------------------------------------------------------------------
// AccessOverrideCard — grant / extend / revoke the vendor's admin
// override that bypasses the standard trial + paid campaign flow.
// ---------------------------------------------------------------------------
const AccessOverrideCard = ({ vendor, onChanged }) => {
  const [days, setDays] = useState(30);
  const [note, setNote] = useState(vendor?.access_override_note || "");
  const [busy, setBusy] = useState(false);

  const active = !!vendor?.access_override_active;
  const label = vendor?.access_override_label || "Standard";
  const mode = vendor?.access_override || null;
  const expires = vendor?.access_override_expires_at;

  const call = async (payload, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.adminSetVendorAccess(vendor.id, payload);
      toast.success(msg);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5" data-testid="admin-vendor-access-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-base font-semibold flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Access override
        </h2>
        <Badge
          variant="outline"
          className={active ? "border-primary text-primary bg-primary/10" : ""}
          data-testid="admin-vendor-access-badge"
        >
          {label}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Complimentary campaign access — replaces payment gating. When active,
        every new campaign is free (payment_status=waived). Vendor&#39;s standard
        15-day trial is preserved for after the override expires.
      </p>
      {expires && mode === "temporary" && (
        <p className="text-xs text-muted-foreground mt-1" data-testid="admin-vendor-access-expires">
          Expires: {new Date(expires).toLocaleString()}
        </p>
      )}
      {vendor?.access_override_note && (
        <p className="text-xs text-muted-foreground mt-1" data-testid="admin-vendor-access-note">
          Note: {vendor.access_override_note}
        </p>
      )}

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Note (why?)
          </label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Extended for Diwali push, req. by Somesh"
            data-testid="admin-vendor-access-note-input"
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Days (for temporary)
          </label>
          <Input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 0))}
            data-testid="admin-vendor-access-days-input"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() => call({ mode: "unlimited", note }, "Unlimited access granted")}
          disabled={busy}
          data-testid="admin-vendor-access-grant-unlimited"
        >
          <InfinityIcon className="h-4 w-4 mr-1" /> Grant unlimited
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => call({ mode: "temporary", days, note }, `Temporary access granted (${days}d)`)}
          disabled={busy}
          data-testid="admin-vendor-access-grant-temporary"
        >
          <TimerReset className="h-4 w-4 mr-1" /> Grant for {days}d
        </Button>
        {active && (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => {
              if (!window.confirm("Revoke the access override? Vendor will fall back to normal paid pricing.")) return;
              call({ mode: "revoke", note }, "Access override revoked");
            }}
            disabled={busy}
            data-testid="admin-vendor-access-revoke"
          >
            <Ban className="h-4 w-4 mr-1" /> Revoke
          </Button>
        )}
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------
// WalletAdjustCard — standing tool for correcting billing errors.
// Positive amount credits the vendor, negative debits. A reason is
// mandatory and lands in db.wallet_transactions for the audit trail.
// Phase 17: adjust now takes a `reason_category` so ops can flag
// billing corrections (real bucket) vs promotional grants (promo
// bucket) vs anything else. Debits always drain promo first via
// core.wallet.try_debit.
// ---------------------------------------------------------------
const REASON_CATEGORY_OPTIONS = [
  { value: "billing_correction", label: "Billing correction (real balance)" },
  { value: "promotional_grant",  label: "Promotional grant (promo balance)" },
  { value: "other",              label: "Other (real balance)" },
];

const WalletAdjustCard = ({ vendor, onChanged }) => {
  const [amountRupees, setAmountRupees] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [reasonCategory, setReasonCategory] = React.useState("billing_correction");
  const [busy, setBusy] = React.useState(false);
  const balancePaise = Number(vendor?.wallet_balance_paise || 0);
  const realPaise = Number(vendor?.wallet_real_balance_paise ?? balancePaise);
  const promoPaise = Number(vendor?.wallet_promo_balance_paise || 0);

  const submit = async () => {
    const rupees = Number.parseFloat(amountRupees);
    if (!Number.isFinite(rupees) || rupees === 0) {
      toast.error("Enter a non-zero amount (positive = credit, negative = debit).");
      return;
    }
    if (!reason.trim()) {
      toast.error("Please write a reason so we have an audit trail.");
      return;
    }
    // Convert rupees to paise, rounding to nearest whole paisa.
    const amountPaise = Math.round(rupees * 100);
    if (reasonCategory === "promotional_grant" && amountPaise < 0) {
      toast.error("Promotional grants must be positive. Use 'billing correction' to debit.");
      return;
    }
    const label = amountPaise > 0 ? "credit" : "debit";
    const bucketNote = reasonCategory === "promotional_grant"
      ? "\n\nThis lands in the PROMO bucket and will NOT count toward franchise revenue when spent."
      : "";
    if (!window.confirm(
      `Apply a ${label} of ₹${Math.abs(amountPaise / 100).toFixed(2)} to ${vendor.business}?\n\nReason: ${reason.trim()}${bucketNote}`
    )) return;
    setBusy(true);
    try {
      const r = await api.superadminAdjustWallet(vendor.id, {
        amount_paise: amountPaise,
        reason: reason.trim(),
        reason_category: reasonCategory,
      });
      const newBal = (r.data?.balance_after_paise || 0) / 100;
      toast.success(`Wallet adjusted — new balance ₹${newBal.toFixed(2)}`);
      setAmountRupees("");
      setReason("");
      setReasonCategory("billing_correction");
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Adjustment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5" data-testid="admin-wallet-adjust-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="font-heading text-base font-semibold flex items-center gap-2">
            <IndianRupee className="h-4 w-4 text-primary" aria-hidden />
            Adjust wallet
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Current balance:{" "}
            <span
              className="font-semibold text-foreground tabular-nums"
              data-testid="admin-wallet-adjust-current"
            >
              ₹{(balancePaise / 100).toFixed(2)}
            </span>
            {" "}·{" "}
            <span className="tabular-nums" data-testid="admin-wallet-split-real">
              Purchased ₹{(realPaise / 100).toFixed(2)}
            </span>
            {" "}·{" "}
            <span className="tabular-nums" data-testid="admin-wallet-split-promo">
              Promo ₹{(promoPaise / 100).toFixed(2)}
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Positive = credit, negative = debit. All adjustments are journaled.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">
            Amount (₹)
          </label>
          <Input
            type="number"
            step="0.01"
            value={amountRupees}
            onChange={(e) => setAmountRupees(e.target.value)}
            placeholder="e.g. 0.30 or -12.50"
            data-testid="admin-wallet-adjust-amount"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">
            Category
          </label>
          <Select
            value={reasonCategory}
            onValueChange={setReasonCategory}
          >
            <SelectTrigger data-testid="admin-wallet-adjust-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REASON_CATEGORY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} data-testid={`admin-wallet-adjust-category-${opt.value}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">
            Reason
          </label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Credit for failed email retargeting charges"
            maxLength={200}
            data-testid="admin-wallet-adjust-reason"
          />
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          onClick={submit}
          disabled={busy}
          data-testid="admin-wallet-adjust-submit"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <IndianRupee className="h-4 w-4 mr-1" />
          )}
          Apply adjustment
        </Button>
      </div>
    </Card>
  );
};


// ---------------------------------------------------------------
// GrantPromoCreditCard — Phase 17 quick-action tool for the Super
// Admin to add promotional wallet credit to a vendor. Lands in the
// promo bucket; will NOT count toward franchise revenue when the
// vendor spends it (only from_real_paise debits count toward
// commission). Uses the same /wallet/adjust endpoint under the
// hood with reason_category="promotional_grant".
// ---------------------------------------------------------------
const GrantPromoCreditCard = ({ vendor, onChanged }) => {
  const [amountRupees, setAmountRupees] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const promoPaise = Number(vendor?.wallet_promo_balance_paise || 0);

  const submit = async () => {
    const rupees = Number.parseFloat(amountRupees);
    if (!Number.isFinite(rupees) || rupees <= 0) {
      toast.error("Enter a positive grant amount in rupees.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Please write a reason (e.g. launch bonus, apology credit).");
      return;
    }
    const amountPaise = Math.round(rupees * 100);
    if (!window.confirm(
      `Grant ₹${(amountPaise / 100).toFixed(2)} promotional credit to ${vendor.business}?\n\n` +
      `Reason: ${reason.trim()}\n\n` +
      `This lands in the PROMO bucket. It will be spent BEFORE any purchased balance, ` +
      `and will NOT count toward franchise revenue when spent.`
    )) return;
    setBusy(true);
    try {
      const r = await api.superadminAdjustWallet(vendor.id, {
        amount_paise: amountPaise,
        reason: reason.trim(),
        reason_category: "promotional_grant",
      });
      const newPromo = (r.data?.promo_paise || 0) / 100;
      toast.success(`Promo credit granted — vendor promo balance is now ₹${newPromo.toFixed(2)}`);
      setAmountRupees("");
      setReason("");
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Grant failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5" data-testid="admin-grant-promo-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="font-heading text-base font-semibold flex items-center gap-2">
            <Gift className="h-4 w-4 text-primary" aria-hidden />
            Grant promotional credit
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Current promo balance:{" "}
            <span
              className="font-semibold text-foreground tabular-nums"
              data-testid="admin-grant-promo-current"
            >
              ₹{(promoPaise / 100).toFixed(2)}
            </span>
            {" "}· Promo balance is spent first. Debits from promo are excluded from franchise revenue.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">
            Grant amount (₹)
          </label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amountRupees}
            onChange={(e) => setAmountRupees(e.target.value)}
            placeholder="e.g. 500"
            data-testid="admin-grant-promo-amount"
          />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">
            Reason
          </label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Launch bonus for early adopters"
            maxLength={200}
            data-testid="admin-grant-promo-reason"
          />
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          onClick={submit}
          disabled={busy}
          data-testid="admin-grant-promo-submit"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Gift className="h-4 w-4 mr-1" />
          )}
          Grant credit
        </Button>
      </div>
    </Card>
  );
};


// ---------------------------------------------------------------------------
// IntakeProfileCard — reads GET /admin/vendors/{id}/intake and renders
// the full Guided-Setup response grouped by the 5 original sections.
// Renders a graceful empty state when the vendor didn't come through
// Guided Setup (404 from the endpoint). No hooks into any mutation
// paths — purely read-only visibility.
// ---------------------------------------------------------------------------

const TAG_STYLE = {
  footfall_focused:    "bg-amber-500/15  text-amber-500  border-amber-500/40",
  bill_value_focused:  "bg-blue-500/15   text-blue-500   border-blue-500/40",
  balanced:            "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
};
const TAG_LABEL = {
  footfall_focused:    "Footfall focused",
  bill_value_focused:  "Bill-value focused",
  balanced:            "Balanced",
};

const PRIMARY_GOAL_LABEL = {
  new_customers:      "Attract new customers",
  repeat_customers:   "Bring customers back more often",
  higher_bill_value:  "Grow bill size",
};
const REWARD_TYPE_LABEL = {
  percent:    "% off",
  flat:       "Flat ₹ off",
  free_addon: "Free add-on",
};
const VALIDATION_LABEL = {
  staff: "Staff manual check",
  scan:  "Scan-based",
};

const IntakeProfileCard = ({ vendorId }) => {
  const [state, setState] = useState({ status: "loading", data: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.adminVendorIntake(vendorId);
        if (!cancelled) setState({ status: "ok", data: r.data });
      } catch (e) {
        if (!cancelled) {
          setState({
            status: e?.response?.status === 404 ? "empty" : "error",
            data: null,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [vendorId]);

  if (state.status === "loading") {
    return (
      <Card className="p-5" data-testid="admin-intake-card-loading">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading intake profile…
        </div>
      </Card>
    );
  }
  if (state.status === "empty") {
    return (
      <Card className="p-5" data-testid="admin-intake-card-empty">
        <h2 className="font-heading text-base font-semibold mb-1">Intake Profile</h2>
        <p className="text-sm text-muted-foreground">
          This vendor signed up via the standard registration flow — no
          Guided Setup answers on file.
        </p>
      </Card>
    );
  }
  if (state.status === "error") {
    return (
      <Card className="p-5" data-testid="admin-intake-card-error">
        <h2 className="font-heading text-base font-semibold mb-1">Intake Profile</h2>
        <p className="text-sm text-red-500">Failed to load intake data.</p>
      </Card>
    );
  }

  const d = state.data;
  const tag = d.profile_tag || "balanced";
  const tagStyle = TAG_STYLE[tag] || TAG_STYLE.balanced;
  const tagLabel = TAG_LABEL[tag] || tag;

  return (
    <Card className="p-5 space-y-5" data-testid="admin-intake-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold">Intake Profile</h2>
          <p className="text-xs text-muted-foreground">
            Guided Setup answered {d.submitted_at ? new Date(d.submitted_at).toLocaleString() : ""}
          </p>
        </div>
        <Badge
          className={`border ${tagStyle} font-bold uppercase tracking-wide`}
          data-testid="admin-intake-tag"
        >
          {tagLabel}
        </Badge>
      </div>

      {d.profile_summary && (
        <p
          className="text-sm text-foreground bg-secondary rounded-md px-3 py-2 leading-snug"
          data-testid="admin-intake-summary"
        >
          {d.profile_summary}
        </p>
      )}

      <IntakeSection title="1. Business basics">
        <IntakeRow label="Business category" value={d.business_category} />
        <IntakeRow label="Average bill / order value" value={d.avg_bill_value} />
        <IntakeRow
          label="Primary goal"
          value={PRIMARY_GOAL_LABEL[d.primary_goal] || d.primary_goal}
        />
      </IntakeSection>

      <IntakeSection title="2. Unconditional coupons (free-spin)">
        <IntakeRow label="Smallest comfortable free reward" value={d.min_free_reward} />
        <IntakeRow label="Existing walk-in offers" value={d.existing_walkin_offers} />
        <IntakeRow label="Max monthly reward budget" value={d.max_monthly_budget} />
        <IntakeRow
          label="Preferred reward format"
          value={REWARD_TYPE_LABEL[d.preferred_reward_type] || d.preferred_reward_type}
        />
      </IntakeSection>

      <IntakeSection title="3. Conditional coupons (bigger rewards)">
        <IntakeRow label="Min bill for bigger reward" value={d.min_bill_for_bonus} />
        <IntakeRow label="Slow-moving products to push" value={d.slow_moving_products} />
        <IntakeRow
          label="Wants repeat-visit reward"
          value={fmtBool(d.wants_repeat_visit_reward)}
        />
        {d.repeat_visit_detail && (
          <IntakeRow label="Repeat-visit reward detail" value={d.repeat_visit_detail} />
        )}
        <IntakeRow
          label="Wants minimum reuse gap"
          value={fmtBool(d.wants_reuse_time_gap)}
        />
        {d.reuse_time_gap_days != null && (
          <IntakeRow label="Reuse gap (days)" value={d.reuse_time_gap_days} />
        )}
      </IntakeSection>

      <IntakeSection title="4. Coupon behavior">
        <IntakeRow label="Coupon expiry (days)" value={d.coupon_expiry_days} />
        <IntakeRow
          label="Wants redemption cap"
          value={fmtBool(d.wants_redemption_cap)}
        />
        {d.redemption_cap_detail && (
          <IntakeRow label="Cap detail" value={d.redemption_cap_detail} />
        )}
        <IntakeRow
          label="Day-of-week reward variation"
          value={fmtBool(d.wants_day_of_week_variation)}
        />
        <IntakeRow
          label="New vs returning differentiation"
          value={fmtBool(d.wants_new_vs_returning_split)}
        />
      </IntakeSection>

      <IntakeSection title="5. Redemption">
        <IntakeRow
          label="Validation method"
          value={VALIDATION_LABEL[d.validation_method] || d.validation_method}
        />
        <IntakeRow
          label="SMS / WhatsApp confirmation"
          value={fmtBool(d.wants_sms_whatsapp_confirmation)}
        />
      </IntakeSection>
    </Card>
  );
};

const IntakeSection = ({ title, children }) => (
  <div>
    <p className="text-xs uppercase text-muted-foreground font-semibold tracking-wider mb-2">
      {title}
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
      {children}
    </div>
  </div>
);
const IntakeRow = ({ label, value }) => (
  <div className="flex flex-col md:flex-row md:justify-between gap-0 md:gap-3 border-b border-border/50 py-1.5">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right break-words">{fmtValue(value)}</span>
  </div>
);
const fmtValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
};
const fmtBool = (v) => {
  if (v === true)  return "Yes";
  if (v === false) return "No";
  return "—";
};

